'use client';

import { addClassName } from '@hc-frontend/core-utils-format';
import { useEffect, useRef, useState } from 'react';

import type { TooltipProps } from './tooltip.types';

export function Tooltip({
  title,
  children,
  className,
  triggerClassName,
}: TooltipProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (showTooltip && triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight,
      };
      const offset = 8;
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      const availableWidth = viewport.width - scrollbarWidth;

      let finalPosition = { top: 0, left: 0 };

      // Try bottom position first
      const bottomPosition = {
        top: triggerRect.bottom + offset,
        left: triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2,
      };

      if (triggerRect.bottom + tooltipRect.height + offset <= viewport.height) {
        finalPosition = bottomPosition;
      } else {
        // Try top position
        const topPosition = {
          top: triggerRect.top - tooltipRect.height - offset,
          left:
            triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2,
        };

        if (triggerRect.top - tooltipRect.height - offset >= 0) {
          finalPosition = topPosition;
        } else {
          // Try right position
          const rightPosition = {
            top:
              triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2,
            left: triggerRect.right + offset,
          };

          if (
            triggerRect.right + tooltipRect.width + offset <=
            availableWidth
          ) {
            finalPosition = rightPosition;
          } else {
            // Use left position
            finalPosition = {
              top:
                triggerRect.top +
                triggerRect.height / 2 -
                tooltipRect.height / 2,
              left: triggerRect.left - tooltipRect.width - offset,
            };
          }
        }
      }

      // Adjust horizontal position to keep tooltip within viewport
      if (finalPosition.left < offset) {
        finalPosition.left = offset;
      } else if (
        finalPosition.left + tooltipRect.width >
        availableWidth - offset
      ) {
        finalPosition.left = availableWidth - tooltipRect.width - offset;
      }

      // Adjust vertical position to keep tooltip within viewport
      if (finalPosition.top < offset) {
        finalPosition.top = offset;
      } else if (
        finalPosition.top + tooltipRect.height >
        viewport.height - offset
      ) {
        finalPosition.top = viewport.height - tooltipRect.height - offset;
      }

      setPosition(finalPosition);
    }
  }, [showTooltip]);

  return (
    <span
      ref={triggerRef}
      className={addClassName('relative', triggerClassName)}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {children}
      {showTooltip && (
        <span
          ref={tooltipRef}
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
          }}
          className={addClassName(
            'fixed z-10 rounded bg-gray-950 px-4-8 py-2-4 text-sm text-text-0 shadow-md min-w-[270px] max-w-[350px]',
            className,
          )}
        >
          {title}
        </span>
      )}
    </span>
  );
}
